import createMediaApi from '@rhg/libs/api/create-media-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery } from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { useCircle } from '../use-circle';

export const programQueryOptions = {
    queryKey: function ({
        programId,
        packClass,
        circleId,
    }: {
        programId: string;
        packClass?: string;
        circleId: number;
    }) {
        return [
            queryKeys.program,
            circleId,
            programId,
            ...(packClass ? [packClass] : []),
        ];
    },
    queryFn: async function ({
        getAccessTokenSilently,
        programId,
        packClass,
        circleId,
        circleAuthorizationToken,
        rHGApp,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        programId: string;
        packClass: string;
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
    }) {
        const mediaApi = createMediaApi(getAccessTokenSilently);

        const response = await mediaApi.getProgram({
            circleAuthorizationToken,
            programId,
            recipientId: circleId,
            _class: packClass,
            xDevicePlatform: 'www',
            rHGApp,
        });

        return response.data;
    },
    queryOptions: function ({
        programId,
        packClass,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
    }: {
        programId: string;
        packClass: string;
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
    }) {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken &&
                programId &&
                packClass
            ),
        };
    },
};

export function useProgram(programId = '', packClass = '', circleId: number) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = programQueryOptions;
    const rHGApp = getRhgAppHeader();

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({ programId, packClass, circleId }),
        queryFn: async () =>
            queryFn({
                getAccessTokenSilently,
                programId,
                packClass,
                circleId,
                circleAuthorizationToken,
                rHGApp,
            }),
        ...queryOptions({
            programId,
            packClass,
            isAuthenticated,
            circleId,
            circleAuthorizationToken,
        }),
    });
    return {
        program: data,
        ...otherProps,
    };
}
